
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../common/Table";
import PageHeader from "../common/PageHeader";
import fetchData from "../../api/fetchApi";
import DatePickerModal from "../common/DatePickerModal";
import { ArrowUpDown } from "lucide-react";

export default function UserManagementPage() {
  const navigate = useNavigate();
  const dir = "rtl";
  
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const [sort, setSort] = useState("-id");
  const [filters, setFilters] = useState({
    name: "",
    username: "",
    email: "",
    phone: "",
    is_active: "",
    is_admin: "",
  });

  // Helper to refresh user list
  const refreshUsers = async () => {
    try {
      const params = new URLSearchParams();
      params.set("page", currentPage);
      params.set("per_page", perPage);
      params.set("sort", sort);
      
      // Handle filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '') {
          params.set(`filter[${key}]`, value);
        }
      });

      const data = await fetchData(`users?${params.toString()}`);
      setUsers(data.data);
      setTotalUsers(data.meta.total);
      setCurrentPage(data.meta.current_page);
      setTotalPages(data.meta.last_page);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  useEffect(() => {
    refreshUsers();
  }, [currentPage, perPage, sort, filters]);

  const handleSortChange = (value) => {
    setSort(value);
    setCurrentPage(1);
  };

  const sortableHeader = (label, sortValue) => (
    <div 
      className="flex items-center gap-2 group cursor-pointer" 
      onClick={(e) => {
        e.stopPropagation();
        handleSortChange(sortValue);
      }}
    >
      <span>{label}</span>
      <button
        type="button"
        className={`p-1 rounded-full text-xs transition-all ${
          sort === sortValue 
            ? "bg-[#1D2035] text-white opacity-100" 
            : "text-gray-400 hover:bg-gray-100 opacity-0 group-hover:opacity-100"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          handleSortChange(sortValue);
        }}
      >
        <ArrowUpDown size={14} />
      </button>
    </div>
  );

  const columns = useMemo(
    () => [
      { header: sortableHeader("المعرف", "-id"), accessor: "id" },
      { header: sortableHeader("الاسم", "-name"), accessor: "name" },
      { header: sortableHeader("اسم المستخدم", "-username"), accessor: "username" },
      { header: "رقم الهاتف", accessor: "phone" },
      { header: sortableHeader("البريد الإلكتروني", "email"), accessor: "email" },
      {
        header: "نشط؟",
        accessor: "is_active",
        render: (row) => (row.is_active ? "نعم" : "لا"),
      },
      {
        header: "مدير؟",
        accessor: "is_admin",
        render: (row) => (row.is_admin ? "نعم" : "لا"),
      },
      {
        header: "تاريخ الإنشاء",
        accessor: "created_at",
        render: (row) =>
          row.created_at ? new Date(row.created_at).toLocaleString() : "-",
      },
      {
        header: "تاريخ التحديث",
        accessor: "updated_at",
        render: (row) =>
          row.updated_at ? new Date(row.updated_at).toLocaleString() : "-",
      },
    ],
    [sort]
  );

  return (
    <div className="min-h-screen p-4 md:p-6 bg-white" dir={dir}>
      <DatePickerModal
        isOpen={isDatePickerOpen}
        onClose={() => setDatePickerOpen(false)}
      />
      {/* Top card: actions + total */}
      <PageHeader title="إجمالي المستخدمين" total={totalUsers} stats={[]}>
      
        <button
          onClick={() => navigate("/admin/users/new")}
          className="px-4 py-2.5 rounded-lg text-white bg-[var(--Primary-Primary)] shadow-sm text-sm md:text-base"
        >
          إضافة مستخدم جديد
        </button>
      </PageHeader>

      {/* Table card */}
      <div className="mt-5">
        <Table
          columns={columns.map(col => {
            if (col.accessor === 'is_active') {
              return { ...col, filterType: 'select', options: [
                { value: '', label: 'الكل' },
                { value: '1', label: 'نشط' },
                { value: '0', label: 'غير نشط' },
              ] };
            }
            if (col.accessor === 'is_admin') {
              return { ...col, filterType: 'select', options: [
                { value: '', label: 'الكل' },
                { value: '1', label: 'مدير' },
                { value: '0', label: 'مستخدم' },
              ] };
            }
            return col;
          })}
          rows={users}
          filters={filters}
          setFilters={setFilters}
          onFilter={refreshUsers}
          actions={[{ label: "عرض الكل" }]}
          pagination={{
            title: "المستخدمين",
            label: `الصفحة ${currentPage} من ${totalPages}`,
            pages: Array.from({ length: totalPages }, (_, i) => i + 1),
            current: currentPage,
            last: totalPages,
            onPageChange: setCurrentPage,
            perPage: perPage,
            onPerPageChange: setPerPage,

            // onRowClick: (row) => navigate(`/admin/users/${row.id}`),
            // onEdit, onStopPublish, onDelete can be passed here for row actions
          }}
          act={[
            {
              label: (
                <div className="flex items-center gap-2">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.33341 2.66646H4.80008C4.05334 2.66646 3.67998 2.66646 3.39476 2.81179C3.14388 2.93962 2.9399 3.14359 2.81207 3.39448C2.66675 3.67969 2.66675 4.05306 2.66675 4.7998V11.1998C2.66675 11.9465 2.66675 12.3199 2.81207 12.6051C2.9399 12.856 3.14388 13.06 3.39476 13.1878C3.67998 13.3331 4.05334 13.3331 4.80008 13.3331H11.2001C11.9468 13.3331 12.3202 13.3331 12.6054 13.1878C12.8563 13.06 13.0603 12.856 13.1881 12.6051C13.3334 12.3199 13.3334 11.9465 13.3334 11.1998V8.33313M10.3334 3.66646L12.219 5.55208M7.17524 6.82464L11.6074 2.39248C12.1281 1.87178 12.9723 1.87178 13.493 2.39248C14.0137 2.91318 14.0137 3.7574 13.493 4.27809L8.91837 8.85274C8.41061 9.3605 8.15673 9.61438 7.86755 9.81629C7.61087 9.9955 7.33403 10.144 7.04273 10.2586C6.71455 10.3878 6.3626 10.4588 5.6587 10.6008L5.33342 10.6665L5.36505 10.445C5.47699 9.66144 5.53296 9.26965 5.66027 8.90387C5.77325 8.5793 5.92758 8.27063 6.11946 7.98551C6.33569 7.66419 6.61554 7.38434 7.17524 6.82464Z"
                      stroke="#1A6AB3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>{" "}
                  <p className="text-[#1A6AB3]">تعديل</p>
                </div>
              ),
              onClick: (row) => navigate(`/admin/users/${row.id}/edit`),
            },
            {
              label: (
                <div className="flex items-center gap-2">
                  <span className="text-green-600">تفعيل المستخدم</span>
                </div>
              ),
              onClick: async (row) => {
                try {
                  await fetchData(`users/${row.id}/activate`, "PUT");
                  await refreshUsers();
                } catch (error) {
                  console.error("Failed to activate user:", error);
                }
              },
            },
            {
              label: (
                <div className="flex items-center gap-2">
                  <span className="text-red-600">تعطيل المستخدم</span>
                </div>
              ),
              onClick: async (row) => {
                try {
                  await fetchData(`users/${row.id}/deactivate`, "PUT");
                  await refreshUsers();
                } catch (error) {
                  console.error("Failed to deactivate user:", error);
                }
              },
            },
         

            {
              label: (
                <div className="flex items-center gap-2">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.6667 3.33325H11.3334L10.6667 2.66659H5.33341L4.66675 3.33325H1.33341M12.6667 3.33325V12.6666C12.6667 13.1199 12.5261 13.5559 12.2192 13.8628C11.9123 14.1697 11.4763 14.3103 11.0231 14.3103H4.97608C4.52285 14.3103 4.08686 14.1697 3.77996 13.8628C3.47307 13.5559 3.33241 13.1199 3.33241 12.6666V3.33325H12.6667Z"
                      stroke="#E02424"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M6.66675 7.33325V11.3333"
                      stroke="#E02424"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M9.33325 7.33325V11.3333"
                      stroke="#E02424"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <p className="text-[#E02424]">حذف المستخدم</p>
                </div>
              ),
              onClick: async (row) => {
                try {
                  await fetchData(`users/${row.id}`, "DELETE");
                  await refreshUsers();
                } catch (error) {
                  // Optionally show error
                  console.error("Failed to delete user:", error);
                }
              },
            }, {
              label: (
                <div className="flex items-center gap-2">
                  <span className="text-blue-600">منح صلاحية المدير</span>
                </div>
              ),
              onClick: async (row) => {
                try {
                  await fetchData(`users/${row.id}/grant-admin`, "PUT");
                  await refreshUsers();
                } catch (error) {
                  console.error("Failed to grant admin:", error);
                }
              },
            },
            {
              label: (
                <div className="flex items-center gap-2">
                  <span className="text-yellow-600">سحب صلاحية المدير</span>
                </div>
              ),
              onClick: async (row) => {
                try {
                  await fetchData(`users/${row.id}/revoke-admin`, "PUT");
                  await refreshUsers();
                } catch (error) {
                  console.error("Failed to revoke admin:", error);
                }
              },
            },
          ]}
        />
      </div>
    </div>
  );
}
